import { API_BASE_URL } from '../config';

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const { exp } = JSON.parse(jsonPayload);
    return exp * 1000 < Date.now();
  } catch (e) {
    console.error('Error parsing token:', e);
    return true;
  }
};

const parseJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

// End the session client-side and send the user to login with a notice.
const redirectToLogin = (message) => {
  localStorage.removeItem('jwtToken');
  localStorage.setItem('loginMessage', message);
  window.location.href = '/on/login';
};

// The backend returns { "error": "readable message" } (sometimes "message")
// for failures; surface that instead of the bare status text.
const extractErrorMessage = async (response) => {
  try {
    const body = await response.json();
    if (body && (body.error || body.message)) return body.error || body.message;
  } catch (e) { /* non-JSON error body */ }
  return null;
};

// Shared non-OK handling: session end on 401, a readable message otherwise.
const throwApiError = async (response) => {
  if (response.status === 401) {
    redirectToLogin('Your session has expired. Please log in again.');
    throw new Error('Session expired. Please log in again.');
  }
  const serverMessage = await extractErrorMessage(response);
  if (response.status === 403) {
    throw new Error(serverMessage || 'You do not have permission to do this.');
  }
  if (response.status >= 500) {
    throw new Error(serverMessage || 'The server ran into a problem. Please try again.');
  }
  throw new Error(serverMessage || `Request failed (${response.status} ${response.statusText})`);
};

// Global request tracking for showing a loading indicator across the app
let __activeApiRequests = 0;
const notifyActiveRequests = () => {
  try {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('api:active', { detail: __activeApiRequests }));
    }
  } catch (_) {
    // no-op
  }
};

// Base fetch function that always includes the bearer token
export const fetchWithAuth = async (endpoint, options = {}) => {
  // silent: true skips the active-request counter, so background polling
  // (e.g. the notification bell) does not flash the global loading bar.
  const { silent, ...fetchOptions } = options;
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  if (isTokenExpired(token)) {
    redirectToLogin('Your session has expired. Please log in again.');
    throw new Error('Session expired. Please log in again.');
  }
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...fetchOptions.headers
  };
  if (!silent) {
    __activeApiRequests += 1;
    notifyActiveRequests();
  }
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include',
      mode: 'cors'
    });
    if (!response.ok) {
      await throwApiError(response);
    }
    if (fetchOptions.method === 'DELETE') {
      return response;
    }
    const acceptHeader = headers['Accept'];
    if (acceptHeader === 'text/html') {
      return response.text();
    } else if (acceptHeader === 'application/pdf' || acceptHeader === 'image/*') {
      return response;
    } else {
      return response.json();
    }
  } finally {
    if (!silent) {
      __activeApiRequests = Math.max(0, __activeApiRequests - 1);
      notifyActiveRequests();
    }
  }
};

// List endpoints return Page<DTO> ({ content, totalElements, ... }) for the table views,
// but the same endpoints are also consumed by dropdowns in form components that need a
// plain array of all rows. This helper accepts either shape.
export const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.content)) return data.content;
  return [];
};

export const fetchWithCorpId = async (endpoint, options = {}) => {
  const corpId = localStorage.getItem('selectedCorpId') || '0';
  return fetchWithAuth(endpoint, {
    ...options,
    headers: {
      'X-Corp-ID': corpId,
      ...options.headers
    }
  });
};

// Fetch a binary resource (e.g. an expense receipt PDF/image) with the bearer
// token + corp header and return it as a Blob for previewing/downloading.
export const fetchBlobWithCorpId = async (endpoint) => {
  const corpId = localStorage.getItem('selectedCorpId') || '0';
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'X-Corp-ID': corpId,
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include',
    mode: 'cors'
  });
  if (!response.ok) {
    if (response.status === 401) {
      redirectToLogin('Your session has expired. Please log in again.');
    }
    const serverMessage = await extractErrorMessage(response);
    throw new Error(serverMessage || `Download failed (${response.status})`);
  }
  return response.blob();
};

export const fetchWithCorpIdAndFormData = async (endpoint, formData, options = {}) => {
  const corpId = localStorage.getItem('selectedCorpId') || '0';
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    throw new Error('Not authenticated');
  }
  if (isTokenExpired(token)) {
    redirectToLogin('Your session has expired. Please log in again.');
    throw new Error('Session expired. Please log in again.');
  }
  const headers = {
    'X-Corp-ID': corpId,
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };
  __activeApiRequests += 1;
  notifyActiveRequests();
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      body: formData,
      credentials: 'include',
      mode: 'cors'
    });
    if (!response.ok) {
      await throwApiError(response);
    }
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      return response.json();
    } else {
      return response.text();
    }
  } finally {
    __activeApiRequests = Math.max(0, __activeApiRequests - 1);
    notifyActiveRequests();
  }
};

// Change the signed-in user's password after verifying the current one. Uses
// the same endpoint as the password reset screen; the username comes from the
// session token so the user does not retype it.
export const changePasswordWithVerification = async (currentPassword, newPassword) => {
  const token = localStorage.getItem('jwtToken');
  const payload = token ? parseJwtPayload(token) : null;
  const userName = payload?.sub;
  if (!userName) {
    throw new Error('Your session is missing account details. Sign out, sign back in, and retry.');
  }
  const response = await fetch(`${API_BASE_URL}/user/changePwd`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userName, oldPassword: currentPassword, newPassword }),
  });
  if (!response.ok) {
    const serverMessage = await extractErrorMessage(response);
    throw new Error(serverMessage || 'Failed to update password.');
  }
  return true;
};
